// src/components/Presupuesto/PresupuestoTableInsights.jsx
import { useState, useContext, useEffect, useMemo, useCallback } from 'react'
import GlobalContext from '../../../../../../context/global-context'

// UI & estilos
import { TablePresupuesto } from './Table'
import { DashboardCard } from '../../../../ui/index'
// import InfoIcon from '@mui/icons-material/Info'
// import InfoVideoModal from './components/InfoVideoModal'
import { colsPresupuesto } from '../../../../data/headersTables'
import CustomButton from '../../../../ui/CustomButton'
// APIs
import { TablesAPI } from '../../../../../../api/table.api'
import { BusinessAPI } from '../../../../../../api/business.api'

// utilidades de jerarquía
import { buildAccountTree } from './utils/hierarchy'

// Constantes de fecha y utilidades
import { currentYear, yearsOptions } from '../../../../../utils/constant'

const PresupuestoTableInsights = () => {
  const { ui, currentBusiness } = useContext(GlobalContext)
  const tablesAPI = new TablesAPI()
  const businessAPI = new BusinessAPI()

  // ─── State ────────────────────────────────────────────────
  const [cuentasOrganizadas, setCuentasOrganizadas] = useState([])
  const [dataTree, setDataTree] = useState([])
  const [inputData, _setInputData] = useState({})
  const [isDirty, setIsDirty] = useState(false)
  const [monthlySumsExt, setMonthlySumsExt] = useState({})
  const [showAll, setShowAll] = useState(false)
  const [expandAll, setExpandAll] = useState(false)
  const [yearFilter, setYearFilter] = useState(currentYear)
  // const [videoOpen, setVideoOpen] = useState(false)

  const setInputData = useCallback(
    newData => {
      _setInputData(newData)
      if (!isDirty) setIsDirty(true)
    },
    [isDirty]
  )

  // ─── Helpers ────────────────────────────────────────────────
  function parseApiPresupuestos(apiData = []) {
    return apiData.map(item => {
      const [code, ...rest] = item.name.split(' ')
      return { ...item, accountCode: code, name: rest.join(' ') || code }
    })
  }

  function organizarCuentasParaTabla(cuentas) {
    cuentas.sort((a, b) => a.codigoFinal.localeCompare(b.codigoFinal))
    const map = {}

    const isExact98 = code => {
      const p = code.split('-')
      return p.length === 4 && p[3] === '98'
    }
    function shrink(code) {
      const seg = code.split('-')
      while (seg.length > 1 && seg[seg.length - 1] === '0') seg.pop()
      return seg.join('-')
    }
    // function parentOf(code) {
    //   const parts = code.split('-')
    //   return parts.length > 1 ? parts.slice(0, -1).join('-') : null
    // }
    function parentOf(code) {
      const parts = code.split('-')
      if (parts.length >= 4 && parts[3] === '98') {
        return parts.slice(0, 3).join('-') // cuelga de nivel 3
      }
      return parts.length > 1 ? parts.slice(0, -1).join('-') : null
    }
    cuentas.forEach(cta => {
      const key = shrink(cta.codigoFinal)
      if (key === '3' || key.startsWith('3-3') || key.startsWith('3-4') || key.startsWith('3-5'))
        return

      if (isExact98(key)) return
      map[key] = map[key] || { accountCode: key, name: cta.nameFinal, subRows: [] }
    })
    Object.keys(map).forEach(code => {
      const p = parentOf(code)
      if (p && map[p]) map[p].subRows.push(map[code])
    })
    return Object.keys(map)
      .filter(code => !parentOf(code) || !map[parentOf(code)])
      .map(code => map[code])
  }

  function mergeCuentasConPresupuestos(cuentas, ppts) {
    return cuentas.map(cta => {
      const p = ppts.find(x => x.accountCode === cta.accountCode) || {}
      const node = {
        ...cta,
        presupuesto: p.presupuesto || [],
        total: p.total || 0
      }
      if (cta.subRows.length) node.subRows = mergeCuentasConPresupuestos(cta.subRows, ppts)
      return node
    })
  }

  function recalcularTotalesPadres(rows) {
    rows.forEach(r => {
      if (!r.subRows.length) return
      recalcularTotalesPadres(r.subRows)
      const meses = Array(12).fill(0)
      let total = 0
      r.subRows.forEach(c => {
        c.presupuesto.forEach((v, i) => (meses[i] += v))
        total += c.total
      })
      r.presupuesto = meses
      r.total = total
    })
  }

  function buildCuerpo(arbol) {
    const mesesCols = colsPresupuesto.slice(2) // ['Enero','Febrero',...]
    const out = []

    const walk = nodes => {
      nodes.forEach(n => {
        const isLeaf = !n.subRows || n.subRows.length === 0
        if (isLeaf) {
          // Sólo hojas → construimos su array de inputs (o 0 si no hay valor)
          const p = mesesCols.map(col => {
            const v = inputData[n.accountCode]?.[col]
            return typeof v === 'number' ? v : 0
          })
          out.push({
            accountCode: n.accountCode,
            name: `${n.accountCode} ${n.name}`,
            presupuesto: p
          })
        }
        // Si tiene hijos, seguimos bajando
        if (n.subRows && n.subRows.length) {
          walk(n.subRows)
        }
      })
    }

    walk(arbol)
    return out
  }

  // ─── Efectos de carga ────────────────────────────────────────
  useEffect(() => {
    if (!currentBusiness?.id) return
    ui.setLoader({ visible: true, text: 'Cargando cuentas…' })
    tablesAPI
      .getCuentaContable(currentBusiness.id)
      .then(resp => {
        const arr = Array.isArray(resp) ? resp : resp.data
        const filtradas = arr.filter(
          c =>
            c.codigoFinal.startsWith('3-') &&
            c.codigoFinal !== '3' &&
            !['3-3', '3-4', '3-5'].includes(c.codigoFinal.slice(0, 3))
        )
        setCuentasOrganizadas(organizarCuentasParaTabla(filtradas))
      })
      .finally(() => ui.setLoader({ visible: false }))
  }, [currentBusiness?.id])

  useEffect(() => {
    if (!currentBusiness?.id) return
    ui.setLoader({ visible: true, text: 'Cargando presupuesto…' })
    tablesAPI
      .getPresupuesto(currentBusiness.id, { type: 'mensual', year: yearFilter })
      .then(r => {
        const parsed = parseApiPresupuestos(r.data)
        const meses = colsPresupuesto.slice(2)
        const initInputs = parsed.reduce((acc, { accountCode, presupuesto }) => {
          const parts = accountCode.split('-')
          const isExact98 = parts.length === 4 && parts[3] === '98'
          const isLeafOrCustom = parts.length >= 4 && !isExact98
          //accountCode.split('-').length === 4
          if (isLeafOrCustom) {
            acc[accountCode] = {}
            presupuesto.forEach((val, i) => {
              acc[accountCode][meses[i]] = val
            })
          }
          return acc
        }, {})
        setInputData(initInputs)
      })
      .finally(() => ui.setLoader({ visible: false }))
  }, [currentBusiness?.id, yearFilter])

  useEffect(() => {
    if (!currentBusiness?.id) return
    ui.setLoader({ visible: true, text: 'Cargando estado extendido…' })
    businessAPI
      .getEstadoResultadoExtend({
        businessId: currentBusiness.id,
        from: `${yearFilter}-01-01`,
        to: `${yearFilter}-12-31`
      })
      .then(({ data: raw }) => {
        const sums = raw.reduce((acc, { codeCuenta, mes, calculoNeto }) => {
          const m = parseInt(mes.split('-')[1], 10) - 1
          acc[codeCuenta] = acc[codeCuenta] || Array(12).fill(0)
          acc[codeCuenta][m] += calculoNeto
          return acc
        }, {})
        setMonthlySumsExt(sums)
        const flat = raw.map(({ codeCuenta, nameCuenta }) => ({ codeCuenta, nameCuenta }))
        setDataTree(buildAccountTree(flat, sums))
      })
      .finally(() => ui.setLoader({ visible: false }))
  }, [currentBusiness?.id, yearFilter])

  // ─── Árbol principal (REAL) ────────────────────────────────
  const fullTree = useMemo(() => {
    const extPpts = Object.entries(monthlySumsExt).map(([accountCode, arr]) => ({
      accountCode,
      presupuesto: arr,
      total: arr.reduce((s, v) => s + v, 0)
    }))
    const m = mergeCuentasConPresupuestos(cuentasOrganizadas, extPpts)
    recalcularTotalesPadres(m)
    return m
  }, [cuentasOrganizadas, monthlySumsExt])

  // ─── Datos para la tabla ───────────────────────────────────
  const tableData = useMemo(() => (showAll ? dataTree : fullTree), [showAll, fullTree, dataTree])

  // ── ¡**aquí** recortamos a sólo dos elementos raíz si vienen tres! ──
  const sanitizedData = useMemo(() => {
    const base = showAll ? dataTree : fullTree
    // si hay más de 2 raíces, me quedo sólo con las primeras 2
    return base.length > 2 ? base.slice(0, 2) : base
  }, [showAll, dataTree, fullTree])

  // ─── Guardar ───────────────────────────────────────────────
  async function handleSave() {
    if (!currentBusiness?.id) return
    ui.setLoader({ visible: true, text: 'Guardando…' })
    const cuerpo = buildCuerpo(tableData)
    await tablesAPI.postPresupuesto(currentBusiness.id, {
      tipoProyeccion: 'mensual',
      year: yearFilter,
      cuerpo
    })
    setIsDirty(false)
    ui.setLoader({ visible: false })
  }

  return (
    <div>
      <DashboardCard className='my-6 font-sans text-text'>
        <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3'>
          <div className='flex flex-col sm:flex-row flex-wrap gap-4 text-sm'>
            <label className='inline-flex items-center space-x-2 text-sm text-text'>
              <input
                type='checkbox'
                checked={showAll}
                onChange={() => setShowAll(v => !v)}
                className='h-5 w-5 rounded border-border text-primary focus:ring-2 focus:ring-primary/20'
              />
              <span className='select-none'>{'Solo cuentas con movimiento'}</span>
            </label>
            <label className='inline-flex items-center space-x-2 text-sm text-text'>
              <input
                type='checkbox'
                checked={expandAll}
                onChange={() => setExpandAll(v => !v)}
                className='h-5 w-5 rounded border-border text-primary focus:ring-2 focus:ring-primary/20'
              />
              <span className='select-none'>{'Expandir todo'}</span>
            </label>
            <div className='flex items-center gap-2 text-sm'>
              <label className='text-muted'>Año</label>
              <select
                value={yearFilter}
                onChange={e => setYearFilter(e.target.value)}
                className='w-[88px] border border-border bg-surface text-text rounded-full px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20'
              >
                {yearsOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <CustomButton
              onClick={handleSave}
              title='Guardar'
              color='primary'
              className='rounded-full'
              disabled={!isDirty}
            />
          </div>
        </div>

        {/* <div className='flex items-center justify-end gap-2 mb-4'>
          <InfoIcon
            fontSize='large'
            onClick={() => setVideoOpen(true)}
            style={{ cursor: 'pointer', marginLeft: 8 }}
          />
        </div> */}

        <div className='pt-4'>
          <TablePresupuesto
            data={sanitizedData}
            inputData={inputData}
            setInputData={setInputData}
            expandAll={expandAll}
          />
        </div>
        {/* <InfoVideoModal open={videoOpen} onClose={() => setVideoOpen(false)} /> */}
      </DashboardCard>
    </div>
  )
}

export default PresupuestoTableInsights
